import { useMemo } from "react";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import MDBox from "components/MDBox";
import type { IHistoricoPedidoItem } from "interfaces/loja/pedidoHistorico";
import { formatDate } from "helpers/formatDate";
import {
  corDotHistoricoCategoria,
  isHistoricoCancelamentoPedido,
  labelHistoricoCategoria,
} from "utils/pedidoHistoricoUi";

export type PedidoHistoricoTimelineProps = {
  /** Registros já ordenados (ex.: cronológico crescente). */
  itens: IHistoricoPedidoItem[];
  /** Carregamento assíncrono opcional (ex.: fetch dedicado no futuro). */
  loading?: boolean;
  /** Texto quando `itens` vazio e não está carregando. */
  emptyText?: string;
  /** Âncora para navegação na página do pedido. */
  anchorId?: string;
};

function fmtQuando(iso: string) {
  try {
    return formatDate(iso, "dd/MM/yyyy HH:mm:ss");
  } catch {
    return iso;
  }
}

function fmtTransicao(
  antes: string | null | undefined,
  depois: string | null | undefined
) {
  const a = antes?.trim() || "—";
  const d = depois?.trim() || "—";
  if (a === "—" && d === "—") return null;
  return `${a} → ${d}`;
}

/**
 * Timeline de auditoria do pedido (histórico persistido no backend).
 * Cobre pagamento, faturamento, separação, logística e retirada conforme `categoria`.
 */
export default function PedidoHistoricoTimeline({
  itens,
  loading = false,
  emptyText = "Nenhum registro de histórico ainda. Ações como pagamento, faturamento e separação passarão a aparecer aqui conforme forem registradas.",
  anchorId = "sec-historico-pedido",
}: PedidoHistoricoTimelineProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const accent = theme.palette.info.main;
  const borderSoft = mode === "dark" ? alpha(accent, 0.42) : alpha(accent, 0.2);

  const ordenados = useMemo(
    () =>
      [...itens].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ),
    [itens]
  );

  return (
    <Card
      id={anchorId}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${borderSoft}`,
        overflow: "hidden",
        boxShadow:
          mode === "dark"
            ? `0 6px 28px ${alpha("#000", 0.28)}`
            : `0 6px 24px ${alpha(accent, 0.07)}`,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          background: `linear-gradient(120deg, ${alpha(accent, mode === "dark" ? 0.12 : 0.07)} 0%, transparent 55%)`,
          borderBottom: `1px solid ${alpha(theme.borders.borderColor, 0.85)}`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(accent, 0.14),
              color: accent,
            }}
          >
            <HistoryEduOutlinedIcon sx={{ fontSize: 24 }} />
          </Box>
          <div>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
            >
              Auditoria
            </Typography>
            <Typography variant="h6" fontWeight={800}>
              Histórico do pedido
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Registros da API: datas, categorias, mudanças de status e usuário
              (quando houver)
            </Typography>
          </div>
        </Stack>
      </Box>
      <MDBox p={2.5} pt={2}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        ) : ordenados.length === 0 ? (
          <MDBox
            py={3}
            px={2}
            sx={{
              borderRadius: 1,
              bgcolor: alpha(
                theme.palette.text.primary,
                mode === "dark" ? 0.06 : 0.04
              ),
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {emptyText}
            </Typography>
          </MDBox>
        ) : (
          <Timeline
            sx={{
              p: 0,
              m: 0,
              [`& .MuiTimelineItem-root:before`]: { flex: 0, padding: 0 },
            }}
          >
            {ordenados.map((it, index) => {
              const transicao = fmtTransicao(it.statusAntes, it.statusDepois);
              const isCancel = isHistoricoCancelamentoPedido(it.acao, it.categoria);
              const dotColor = isCancel
                ? "error"
                : corDotHistoricoCategoria(it.categoria);
              const nomeUsuario =
                it.usuario?.nome?.trim() ||
                it.usuario?.email?.trim() ||
                (it.usuario?.id ? `${it.usuario.id.slice(0, 8)}…` : null);

              if (isCancel) {
                return (
                  <TimelineItem
                    key={it.id}
                    sx={{
                      minHeight: 88,
                      gap: 2.5,
                      alignItems: "stretch",
                    }}
                  >
                    <TimelineSeparator sx={{ flexShrink: 0 }}>
                      <TimelineDot color={dotColor} variant="filled" />
                      {index < ordenados.length - 1 ? (
                        <TimelineConnector />
                      ) : null}
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{
                        py: "12px",
                        px: 0,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Stack spacing={0.75}>
                        <Typography variant="body2" fontWeight={800} color="error">
                          Cancelamento do pedido
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Data: {fmtQuando(it.createdAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Box component="span" fontWeight={700}>
                            Responsável:{" "}
                          </Box>
                          {nomeUsuario ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <Box component="span" fontWeight={700}>
                            Motivo:{" "}
                          </Box>
                          {it.descricao?.trim() || "—"}
                        </Typography>
                        {transicao ? (
                          <Typography variant="caption" color="text.secondary">
                            Status: {transicao}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TimelineContent>
                  </TimelineItem>
                );
              }

              return (
                <TimelineItem
                  key={it.id}
                  sx={{
                    minHeight: 72,
                    gap: 2.5,
                    alignItems: "stretch",
                  }}
                >
                  <TimelineSeparator sx={{ flexShrink: 0 }}>
                    <TimelineDot color={dotColor} variant="outlined" />
                    {index < ordenados.length - 1 ? (
                      <TimelineConnector />
                    ) : null}
                  </TimelineSeparator>
                  <TimelineContent
                    sx={{
                      py: "12px",
                      px: 0,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Stack spacing={0.5}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        {fmtQuando(it.createdAt)}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {labelHistoricoCategoria(it.categoria)} · {it.acao}
                      </Typography>
                      {transicao ? (
                        <Typography variant="caption" color="text.secondary">
                          Status: {transicao}
                        </Typography>
                      ) : null}
                      {it.descricao?.trim() ? (
                        <Typography variant="body2" color="text.secondary">
                          {it.descricao.trim()}
                        </Typography>
                      ) : null}
                      {it.usuario?.nome?.trim() || it.usuario?.email?.trim() ? (
                        <Typography variant="caption" color="text.secondary">
                          Usuário:{" "}
                          {it.usuario.nome?.trim() ||
                            it.usuario.email?.trim() ||
                            it.usuario.id.slice(0, 8) + "…"}
                        </Typography>
                      ) : null}
                    </Stack>
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>
        )}
      </MDBox>
    </Card>
  );
}
